import { memo, useCallback, useEffect, useState } from "react";
import { Modal, Spin } from "antd";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getApiRequest } from "../../../services/getApiRequest";
import { END_POINTS } from "../../../Helper/Constant";
import { showToast } from "../ToasterMessage/ToasterMessage";
import goatIcon from "../../../assets/icons/full-goat.png";
import coinIcon from "../../../assets/icons/header-coin.svg";
import "./SendGiftTypeModal.scss";

const GIFT_ICONS = {
  goat: goatIcon,
};

const parseGiftTypes = (response) => {
  if (Array.isArray(response?.giftTypes)) return response.giftTypes;
  if (Array.isArray(response?.gifts)) return response.gifts;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const SendGiftTypeModal = ({ open, onClose, category, receiverId }) => {
  const { t, i18n } = useTranslation("common");
  const navigate = useNavigate();
  const [giftTypes, setGiftTypes] = useState([]);
  const [loading, setLoading] = useState(false);

  const getGiftName = useCallback(
    (name) => {
      if (!name) return "";
      if (typeof name === "string") return name;
      return i18n.language === "ar" ? name.ar || name.en : name.en || name.ar;
    },
    [i18n.language]
  );

  useEffect(() => {
    if (!open || !category) {
      setGiftTypes([]);
      return;
    }

    const fetchGiftTypes = async () => {
      setLoading(true);
      try {
        const response = await getApiRequest(
          `${END_POINTS.GET_GIFT_TYPES}?category=${category}`
        );
        if (response?.success) {
          setGiftTypes(parseGiftTypes(response));
        } else {
          setGiftTypes([]);
        }
      } catch (error) {
        setGiftTypes([]);
        showToast(error?.response?.data?.message || error?.error?.message, "error");
      } finally {
        setLoading(false);
      }
    };

    fetchGiftTypes();
  }, [open, category]);

  const handleSelectGift = useCallback(
    (gift) => {
      if (!receiverId || !gift?._id) return;
      onClose();
      const params = new URLSearchParams({ giftTypeId: gift._id });
      if (category) params.set("category", category);
      navigate(`/send-gift/${receiverId}?${params.toString()}`);
    },
    [receiverId, category, navigate, onClose]
  );

  const title =
    category === "roast"
      ? t("user_profile.send_roast", "Send Roast")
      : t("user_profile.send_hype", "Send Hype");

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      centered
      destroyOnClose
      className="send-gift-type-modal"
      title={title}
    >
      <Spin spinning={loading}>
        {giftTypes.length > 0 ? (
          <div className="send-gift-type-grid">
            {giftTypes.map((gift) => {
              const giftName = getGiftName(gift?.name);
              const image =
                gift?.thumbnailUrl ||
                gift?.mediaUrl ||
                GIFT_ICONS[gift?.slug] ||
                goatIcon;

              return (
                <button
                  type="button"
                  className="send-gift-type-card"
                  key={gift?._id || gift?.slug}
                  onClick={() => handleSelectGift(gift)}
                >
                  <img
                    src={image}
                    alt={giftName}
                    className="send-gift-type-card__img"
                    loading="lazy"
                  />
                  {giftName && (
                    <p className="send-gift-type-card__name">{giftName}</p>
                  )}
                  <span className="send-gift-type-card__price">
                    <img src={coinIcon} alt="" loading="lazy" />
                    {Number(gift?.price || 0).toLocaleString()}
                  </span>
                </button>
              );
            })}
          </div>
        ) : (
          !loading && (
            <p className="send-gift-type-empty">
              {t("user_profile.no_gifts_found", "No gifts found.")}
            </p>
          )
        )}
      </Spin>
    </Modal>
  );
};

SendGiftTypeModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  category: PropTypes.oneOf(["roast", "hype"]),
  receiverId: PropTypes.string,
};

export default memo(SendGiftTypeModal);
